<template>
  <div class="bg-light-white">
    <GoBackButtonComponent :path="'/login'" />
  </div>
  
  <div class="h-screen bg-light-white flex flex-col items-center">
    <section class="m-auto px-2 md:px-0 text-secondary" :class="classToUse">
      <LoadingDialog :visible="loading" />
      
      <div class="rounded shadow p-3 text-center bg-white pb-10">
        <LogoComponent :customClass="'text-4xl md:text-6xl'" />
        
        <div>
          <p class="text-2xl font-bold py-4">
            {{ translate('Create new password', 'ucfirst') }}
          </p>
          <p class="text-gray-600 mb-6 text-sm">
            {{ translate('Type your new password below to reset your access.', 'ucfirst') }}
          </p>

          <div class="flex flex-col text-left gap-2 mb-4">
            <span class="text-gray-600 block text-sm font-medium">{{ translate('new password', 'ucfirst') }}:</span>
            <Password 
              v-model="password"
              class="w-full"
              :input-class="'w-full py-2 px-3 rounded-lg border border-color-iron border-focus-purple-suit'"
              :placeholder="translate('password', 'ucfirst')"
              :disabled="loading"
              :feedback="false"
              toggleMask
            />
          </div>

          <div class="flex flex-col text-left gap-2 mb-6">
            <span class="text-gray-600 block text-sm font-medium">{{ translate('confirm password', 'ucfirst') }}:</span>
            <Password 
              v-model="confirmPassword"
              class="w-full"
              :input-class="'w-full py-2 px-3 rounded-lg border border-color-iron border-focus-purple-suit'"
              :placeholder="translate('confirm password', 'ucfirst')"
              :disabled="loading"
              :feedback="false"
              toggleMask
            />
          </div>

          <Button 
            class="w-full bg-purple-suit-forced text-white p-1"
            :label="translate('save new password', 'capitalize')"
            @click.prevent="submitResetPassword()"
            :loading="loading"
            :disabled="!isFormValid()"
          />
        </div>

        <p class="mt-5 cursor-pointer text-sm font-medium hover:underline transition">
          <router-link :to="'/login'">
            {{ translate('back to login', 'ucfirst') }}
          </router-link>
        </p>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import Button from 'primevue/button';
import Password from 'primevue/password';
import { translate } from '@/composables/translate';
import { useRoute } from 'vue-router';
import { isScreeSmall } from '@/composables/windowSize';
import cookiesHandler from '@/composables/cookiesHandler';
import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
import UserService from '@/services/UserService';
import { showToast } from '@/composables/toast';
import { useToast } from 'primevue/usetoast';
import urlHandler from '@/composables/urlHandler';
import GoBackButtonComponent from '@/components/misc/GoBackButtonComponent.vue';
import LogoComponent from '@/components/LogoComponent.vue';

const route = useRoute();
const toast = useToast();

const breakPoint = 500;
const loading = ref(false);

// Parâmetros da URL obtidos via query string
const userEmail = ref('');
const verificationCode = ref('');

// Campos de senha
const password = ref('');
const confirmPassword = ref('');

const classToUse = ref('');
const screenSize = ref(window.innerWidth);

onMounted(() => {
    getRouteParameters();
    window.addEventListener('resize', updateScreenSize);
    updateScreenSize();
});

onUnmounted(() => {
    window.removeEventListener('resize', updateScreenSize);
});

const updateScreenSize = () => {
    screenSize.value = window.innerWidth;
    classToUse.value = isScreeSmall(breakPoint) ? 'w-full' : 'm-form-w';
};

function getRouteParameters() {
    userEmail.value = route.query?.email || '';
    verificationCode.value = route.query?.code || '';

    if (!userEmail.value || !verificationCode.value) {
      showToast(toast, translate('invalid or expired password reset link', 'ucfirst'), 'error');
      urlHandler.redirectAndScroll('/login');
    }
}

function isFormValid() {
    return password.value.length > 0 && confirmPassword.value.length > 0;
}

const submitResetPassword = async () => {
    if (!isFormValid()) return;

    if (password.value !== confirmPassword.value) {
        showToast(toast, translate('passwords do not match', 'ucfirst'), 'error');
        return;
    }

    const passValidation = UserService.checkPassword(password.value);
    if (!passValidation.success) {
        showToast(toast, translate(passValidation.message, 'ucfirst'), 'error');
        return;
    }

    loading.value = true;
    
    const response = await UserService.updatePasswordWithCode(
      userEmail.value, 
      verificationCode.value, 
      password.value
    );
    
    loading.value = false;

    if (!response.success()) {
      showToast(toast, translate(response.getMessage(), 'ucfirst'), 'error');
      if (response.getCode() == response.definedCodes.EXPIRED_VERIFICATION_CODE) {
        urlHandler.redirectAndScroll('/forgot-password', {email: userEmail.value, code: verificationCode.value});
      }
      return;
    }

    cookiesHandler.delete('TM_PWD_RESET_SENT');

    showToast(toast, translate('password changed successfully', 'success'));
    urlHandler.redirectAndScroll('/login');
};
</script>

<style scoped>
/* Mantido o padrão visual do seu sistema */
</style>